/**
 * Worksheet Edit Page
 * Munkalap szerkesztése
 */

import {
  PRIORITY_LABEL_KEYS,
  STATUS_LABEL_KEYS,
  WorksheetPriority,
  WorksheetStatus,
} from '@/api/worksheets';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProcurementRequestCard } from '@/components/worksheet/ProcurementRequestCard';
import { useProcurementRequests } from '@/hooks/use-procurement-requests';
import { useWorksheet, useWorksheetMutations } from '@/hooks/use-worksheets';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface FormData {
  diagnosis: string;
  workPerformed: string;
  internalNote: string;
  priority: WorksheetPriority;
  estimatedCompletionDate: string;
  costLimit: number | undefined;
  deviceName: string;
  deviceSerialNumber: string;
  faultDescription: string;
}

export function WorksheetEditPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { worksheet, isLoading, error, refetch } = useWorksheet(id);
  const { updateWorksheet, changeStatus, isLoading: isSaving } = useWorksheetMutations();
  const [formData, setFormData] = useState<FormData>({
    diagnosis: '',
    workPerformed: '',
    internalNote: '',
    priority: WorksheetPriority.NORMAL,
    estimatedCompletionDate: '',
    costLimit: undefined,
    deviceName: '',
    deviceSerialNumber: '',
    faultDescription: '',
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingCloseStatus, setPendingCloseStatus] = useState<WorksheetStatus | null>(null);

  // S-126-04 slice 2: count open ProcurementRequests for close-gate warning.
  // Phase 1 default = warning (Q-126-01 ratify pending for hard-block upgrade).
  const { requests: procurementRequests } = useProcurementRequests(
    worksheet?.id ? { worksheetId: worksheet.id } : {}
  );
  const openProcurementCount = procurementRequests.filter(
    r => r.status !== 'RECEIVED' && r.status !== 'CANCELLED'
  ).length;

  // Initialize form when worksheet loads
  useEffect(() => {
    if (worksheet) {
      setFormData({
        diagnosis: worksheet.diagnosis || '',
        workPerformed: worksheet.workPerformed || '',
        internalNote: worksheet.internalNote || '',
        priority: worksheet.priority as WorksheetPriority,
        estimatedCompletionDate: worksheet.estimatedCompletionDate || '',
        costLimit: worksheet.costLimit,
        deviceName: worksheet.deviceName,
        deviceSerialNumber: worksheet.deviceSerialNumber || '',
        faultDescription: worksheet.faultDescription,
      });
    }
  }, [worksheet]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!worksheet) return;

    setSaveError(null);
    try {
      await updateWorksheet(worksheet.id, {
        deviceName: formData.deviceName,
        deviceSerialNumber: formData.deviceSerialNumber || undefined,
        faultDescription: formData.faultDescription,
        diagnosis: formData.diagnosis || undefined,
        workPerformed: formData.workPerformed || undefined,
        priority: formData.priority,
        costLimit: formData.costLimit,
        estimatedCompletionDate: formData.estimatedCompletionDate || undefined,
        internalNote: formData.internalNote || undefined,
      });
      navigate(`/worksheet/${id}`);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('service.saveError'));
    }
  };

  const performStatusChange = async (newStatus: WorksheetStatus) => {
    if (!worksheet) return;
    try {
      await changeStatus(worksheet.id, newStatus);
      refetch();
    } catch (err) {
      console.error('Status change failed:', err);
    }
  };

  const handleStatusChange = async (newStatus: WorksheetStatus) => {
    if (!worksheet) return;
    // S-126-04 slice 2: close-gate warning when closing a worksheet that
    // still has open ProcurementRequest rows (anything not RECEIVED/CANCELLED).
    if (newStatus === WorksheetStatus.KESZ && openProcurementCount > 0) {
      setPendingCloseStatus(newStatus);
      return;
    }
    await performStatusChange(newStatus);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-muted-foreground ml-3">{t('common.loading')}</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="p-6 text-center">
          <p className="text-red-500 mb-4">{t('service.errorPrefix', { message: error })}</p>
          <Button onClick={() => refetch()}>{t('common.retry')}</Button>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!worksheet) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="p-6 text-center">
          <p className="text-muted-foreground mb-4">{t('service.worksheetNotFound')}</p>
          <Button onClick={() => navigate('/worksheet')}>{t('common.backToList')}</Button>
        </Card>
      </div>
    );
  }

  const isEditable = !['LEZART', 'TOROLVE'].includes(worksheet.status);

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(`/worksheet/${id}`)}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('service.editWorksheet')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {worksheet.worksheetNumber}
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                worksheet.status === 'FELVEVE'
                  ? 'bg-blue-100 text-blue-800'
                  : worksheet.status === 'FOLYAMATBAN'
                    ? 'bg-yellow-100 text-yellow-800'
                    : worksheet.status === 'KESZ'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-muted text-foreground'
              }`}
            >
              {t(STATUS_LABEL_KEYS[worksheet.status as WorksheetStatus]) || worksheet.status}
            </span>
          </div>
          {isEditable && (
            <Button
              onClick={handleSubmit}
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        {!isEditable && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/30">
            <p className="text-amber-800 dark:text-amber-200">
              {t('service.worksheetNotEditable', {
                status:
                  t(STATUS_LABEL_KEYS[worksheet.status as WorksheetStatus])?.toLowerCase() ||
                  worksheet.status,
              })}
            </p>
          </div>
        )}

        {saveError && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
            <p className="text-red-800 dark:text-red-200">{saveError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Device Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('service.deviceData')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.deviceNameRequired')}
                  </label>
                  <Input
                    value={formData.deviceName}
                    onChange={e => updateField('deviceName', e.target.value)}
                    disabled={!isEditable}
                    placeholder={t('service.deviceNamePlaceholderShort')}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.serialNumber')}
                  </label>
                  <Input
                    value={formData.deviceSerialNumber}
                    onChange={e => updateField('deviceSerialNumber', e.target.value)}
                    disabled={!isEditable}
                    placeholder={t('service.serialNumberPlaceholderShort')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('service.worksheetSettings')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.priorityField')}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={e => updateField('priority', e.target.value as WorksheetPriority)}
                    disabled={!isEditable}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  >
                    {Object.entries(PRIORITY_LABEL_KEYS).map(([value, labelKey]) => (
                      <option key={value} value={value}>
                        {t(labelKey)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.expectedCompletion')}
                  </label>
                  <Input
                    type="date"
                    value={formData.estimatedCompletionDate}
                    onChange={e => updateField('estimatedCompletionDate', e.target.value)}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.costLimitFt')}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.costLimit ?? ''}
                    onChange={e =>
                      updateField('costLimit', e.target.value ? Number(e.target.value) : undefined)
                    }
                    disabled={!isEditable}
                    placeholder={t('service.costLimitPlaceholder')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fault Description */}
          <Card>
            <CardHeader>
              <CardTitle>{t('service.faultDescription')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.faultDescription}
                onChange={e => updateField('faultDescription', e.target.value)}
                rows={4}
                disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 bg-white p-3 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder={t('service.faultDescriptionCustomerPlaceholder')}
                required
              />
            </CardContent>
          </Card>

          {/* Diagnosis & Work */}
          <Card>
            <CardHeader>
              <CardTitle>{t('service.diagnosisAndWork')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.diagnosis')}
                  </label>
                  <textarea
                    value={formData.diagnosis}
                    onChange={e => updateField('diagnosis', e.target.value)}
                    rows={4}
                    disabled={!isEditable}
                    className="w-full rounded-md border border-gray-300 bg-white p-3 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    placeholder={t('service.diagnosisCausePlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('service.completedWork')}
                  </label>
                  <textarea
                    value={formData.workPerformed}
                    onChange={e => updateField('workPerformed', e.target.value)}
                    rows={4}
                    disabled={!isEditable}
                    className="w-full rounded-md border border-gray-300 bg-white p-3 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    placeholder={t('service.workPerformedPlaceholder')}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Procurement requests (S-126-04) */}
          <ProcurementRequestCard worksheetId={worksheet.id} disabled={!isEditable} />

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>{t('service.internalNote')}</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={formData.internalNote}
                onChange={e => updateField('internalNote', e.target.value)}
                rows={3}
                disabled={!isEditable}
                className="w-full rounded-md border border-gray-300 bg-white p-3 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                placeholder={t('service.internalNotePlaceholder')}
              />
            </CardContent>
          </Card>

          {/* Status Actions */}
          {isEditable && (
            <Card>
              <CardHeader>
                <CardTitle>{t('service.statusChangeTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {worksheet.status === 'FELVEVE' && (
                    <Button
                      type="button"
                      onClick={() => handleStatusChange(WorksheetStatus.FOLYAMATBAN)}
                      disabled={isSaving}
                    >
                      {t('service.startWork')}
                    </Button>
                  )}
                  {worksheet.status === 'FOLYAMATBAN' && (
                    <>
                      <Button
                        type="button"
                        onClick={() => handleStatusChange(WorksheetStatus.VARHATO)}
                        variant="outline"
                        disabled={isSaving}
                      >
                        {t('service.statusLabels.waiting')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleStatusChange(WorksheetStatus.KESZ)}
                        disabled={isSaving}
                      >
                        {t('status.completed')}
                      </Button>
                    </>
                  )}
                  {worksheet.status === 'VARHATO' && (
                    <>
                      <Button
                        type="button"
                        onClick={() => handleStatusChange(WorksheetStatus.FOLYAMATBAN)}
                        variant="outline"
                        disabled={isSaving}
                      >
                        {t('service.continueWork')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleStatusChange(WorksheetStatus.KESZ)}
                        disabled={isSaving}
                      >
                        {t('status.completed')}
                      </Button>
                    </>
                  )}
                  {worksheet.status === 'KESZ' && (
                    <Button
                      type="button"
                      onClick={() => handleStatusChange(WorksheetStatus.SZAMLAZANDO)}
                      disabled={isSaving}
                    >
                      {t('service.toBilling')}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          {isEditable && (
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/worksheet/${id}`)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={isSaving} className="bg-primary hover:bg-primary/90">
                {isSaving ? t('common.saving') : t('service.saveChanges')}
              </Button>
            </div>
          )}
        </form>
      </main>

      {/* S-126-04 slice 2: Worksheet close-gate warning dialog */}
      <Dialog
        open={pendingCloseStatus !== null}
        onOpenChange={open => {
          if (!open) setPendingCloseStatus(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('procurement.closeGate.title')}</DialogTitle>
            <DialogDescription>
              {t('procurement.closeGate.warning', { count: openProcurementCount })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingCloseStatus(null)}
              data-testid="close-gate-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={async () => {
                const next = pendingCloseStatus;
                setPendingCloseStatus(null);
                if (next) await performStatusChange(next);
              }}
              data-testid="close-gate-proceed"
            >
              {t('procurement.closeGate.proceed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
